/**
 * Role-based permissions for the Marva platform
 *
 * Roles:
 * - super_admin: Marva internal team - full access to all accounts + admin dashboard
 * - owner: Full access to all features, billing, and workspace management
 * - admin: Can manage integrations, view all data, manage webhooks, invite team
 * - member: Read-only access to data and chat
 */

import { ROLE_REASONS } from '@marva/shared/auth-vocabulary'

export type Role = 'super_admin' | 'owner' | 'admin' | 'member'

export type Feature = 'dashboard' | 'chat' | 'messaging' | 'data' | 'account' | 'docs' | 'attribution' | 'activity' | 'admin'

export type Permission =
  | 'view_chat'
  | 'view_data'
  | 'export_data'
  | 'view_setup'
  | 'manage_integrations'
  | 'manage_webhooks'
  | 'view_team'
  | 'manage_team'
  | 'invite_members'
  | 'view_docs'
  | 'view_attribution'
  | 'manage_billing'
  | 'manage_workspace'
  | 'create_workspace'
  | 'view_admin_dashboard'

// Define which permissions each role has
const rolePermissions: Record<Role, Permission[]> = {
  super_admin: [
    'view_chat',
    'view_data',
    'export_data',
    'view_setup',
    'manage_integrations',
    'manage_webhooks',
    'view_team',
    'manage_team',
    'invite_members',
    'view_docs',
    'view_attribution',
    'manage_billing',
    'manage_workspace',
    'create_workspace',
    'view_admin_dashboard',
  ],
  owner: [
    'view_chat',
    'view_data',
    'export_data',
    'view_setup',
    'manage_integrations',
    'manage_webhooks',
    'view_team',
    'manage_team',
    'invite_members',
    'view_docs',
    'view_attribution',
    'manage_billing',
    'manage_workspace',
    'create_workspace',
  ],
  admin: [
    'view_chat',
    'view_data',
    'export_data',
    'view_setup',
    'manage_integrations',
    'manage_webhooks',
    'view_team',
    'invite_members',
    'view_docs',
    'view_attribution',
    'create_workspace',
  ],
  member: [
    'view_chat',
    'view_data',
    'view_docs',
  ],
}

// Define which features each role can access (for navigation)
const featureAccess: Record<Role, Feature[]> = {
  super_admin: ['dashboard', 'chat', 'messaging', 'data', 'attribution', 'activity', 'account', 'docs', 'admin'],
  owner: ['dashboard', 'chat', 'messaging', 'data', 'attribution', 'activity', 'account', 'docs'],
  admin: ['dashboard', 'chat', 'messaging', 'data', 'attribution', 'activity', 'account', 'docs'],
  member: ['dashboard', 'chat', 'data', 'docs'],
}

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: Role | undefined | null, permission: Permission): boolean {
  if (!role) return false
  return rolePermissions[role]?.includes(permission) ?? false
}

/**
 * Check if a role can access a specific feature/view
 */
export function canAccessFeature(role: Role | undefined | null, feature: Feature): boolean {
  if (!role) return false
  return featureAccess[role]?.includes(feature) ?? false
}

/**
 * Get all features accessible to a role
 */
export function getAccessibleFeatures(role: Role | undefined | null): Feature[] {
  if (!role) return []
  return featureAccess[role] ?? []
}

/**
 * Check if user can manage team (owner or admin)
 */
export function canManageTeam(role: Role | undefined | null): boolean {
  return hasPermission(role, 'manage_team') || hasPermission(role, 'invite_members')
}

/**
 * The Users API membership contract (MAR-250), as the UI should OFFER it. The server's
 * `assertWorkspaceRole` is the authority; these only keep the UI from offering what it refuses.
 *
 * - invite `admin`/`member`: owner or admin
 * - invite `owner`, change any role, remove another member: owner (`manage_team`)
 * - leave (remove yourself): anyone, subject to the last-owner rule
 */
export type MemberRole = 'owner' | 'admin' | 'member'

export function invitableRoles(role: Role | undefined | null): MemberRole[] {
  if (hasPermission(role, 'manage_team')) return ['member', 'admin', 'owner']
  if (hasPermission(role, 'invite_members')) return ['member', 'admin']
  return []
}

export function canChangeMemberRoles(role: Role | undefined | null): boolean {
  return hasPermission(role, 'manage_team')
}

export function canRemoveMembers(role: Role | undefined | null): boolean {
  return hasPermission(role, 'manage_team')
}

/**
 * Check if user can manage setup/integrations
 */
export function canManageSetup(role: Role | undefined | null): boolean {
  return hasPermission(role, 'manage_integrations') || hasPermission(role, 'manage_webhooks')
}

/**
 * Check if user can export data
 */
export function canExportData(role: Role | undefined | null): boolean {
  return hasPermission(role, 'export_data')
}

/**
 * Check if user can create workspaces
 */
export function canCreateWorkspace(role: Role | undefined | null): boolean {
  return hasPermission(role, 'create_workspace')
}

/**
 * Check if user is a super admin (Marva internal team)
 */
export function isSuperAdmin(role: Role | undefined | null): boolean {
  return role === 'super_admin'
}

const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  super_admin: 'Marva staff',
}

/**
 * The words a role is SHOWN as — the one place a gate value becomes display text (PR #237
 * round 5: the badge printed the raw `super_admin`). `null` for no role, or a value outside
 * `Role`: the caller hides the badge / shows "—" rather than guessing one.
 */
export function roleLabel(role: Role | undefined | null): string | null {
  if (!role) return null
  return Object.prototype.hasOwnProperty.call(ROLE_LABELS, role) ? ROLE_LABELS[role] : null
}

/**
 * Check if user can access admin dashboard
 */
export function canAccessAdminDashboard(role: Role | undefined | null): boolean {
  return hasPermission(role, 'view_admin_dashboard')
}

/**
 * Just enough of `UserMembership` to answer a role question.
 *
 * Structural rather than imported, so this module stays free of the store — it is the
 * vocabulary a store, a screen or a test can all speak.
 */
export interface MembershipForRole {
  isSuperAdmin?: boolean
  accounts?: { workspaces?: { workspace?: { id?: string }; userRole?: string; userId?: string }[] }[]
}

/** Higher wins; a role outside the vocabulary ranks below `member`. Mirrors the server's
 * `ROLE_PRECEDENCE` in `packages/shared/src/middleware/authorize.js`. */
const ROLE_PRECEDENCE: Record<string, number> = { owner: 3, admin: 2, member: 1 }

/**
 * The caller's role in ONE named workspace, or `null` when they have none there.
 *
 * ⚠️ **`null` means "no role", and the UI offers nothing on it.** This is the ONLY place a
 * surface reads the caller's role from. Since MAR-250 there is no rosterless-owner fallback
 * server-side — a caller with no `users` row in a workspace is refused by
 * `assertWorkspaceRole` (`no_workspace_membership`) — and there is none here either: no row
 * in the membership, no role. Workspace creation and the Shopify install both write an
 * explicit `owner` row, so every legitimate owner has one.
 *
 * ⚠️ **No `currentUser` fallback (MAR-250 review round 2).** There used to be one, to cover
 * the window right after `POST /workspaces` when the eventually-consistent memberships index
 * did not list the creator's row yet. It also kept a departed owner's stale `currentUser`
 * answering "owner" until reload. The post-create window is now covered at the source:
 * `workspaceService` merges the row the create response carries into `userMembership`. And
 * a surface that is refused `no_workspace_membership` / `insufficient_workspace_role`
 * (`isStaleRoleRefusal`) refreshes the membership, so a lost row stops answering.
 * (`useWorkspaceRole` reads this and nothing else — not `currentUser` even with no membership
 * loaded; PR #237 round 4 removed a fallback for that state. See its docblock.)
 *
 * ⚠️ **Legacy duplicate rows resolve as the server resolves them.** A person can hold more
 * than one row in a workspace; `assertWorkspaceRole` takes the highest-privilege one
 * (owner > admin > member), ties broken by the lowest `id`. So does this — never the first
 * row in index order, which would let the UI and the server disagree about the same person.
 *
 * ⚠️ And it is asked **about a named workspace**, deliberately — not read off
 * `currentUser.role`, which is a "current workspace" value written by `switchWorkspace` and
 * can describe a different workspace than the one on screen.
 *
 * ⚠️ None of this is authorisation. Knowledge erasure, the Users API and, since MAR-170,
 * `PUT /workspaces/{id}` check roles server-side. This helper decides what the UI should OFFER;
 * it never replaces the owning endpoint's authorization.
 */
export function roleForWorkspace(
  membership: MembershipForRole | null | undefined,
  workspaceId: string | null | undefined
): Role | null {
  if (!membership || !workspaceId) return null
  // Marva staff, the same bypass `middleware/authorize.js` gives `isMarvaAdmin`.
  if (membership.isSuperAdmin) return 'super_admin'
  let best: { userRole?: string; userId?: string } | undefined
  for (const account of membership.accounts || []) {
    for (const entry of account.workspaces || []) {
      if (entry?.workspace?.id !== workspaceId || !entry.userRole) continue
      if (!best) {
        best = entry
        continue
      }
      const rank = ROLE_PRECEDENCE[entry.userRole] || 0
      const bestRank = ROLE_PRECEDENCE[best.userRole as string] || 0
      if (rank > bestRank || (rank === bestRank && String(entry.userId) < String(best.userId))) {
        best = entry
      }
    }
  }
  return (best?.userRole as Role) || null
}

/**
 * Does this refusal say the caller's role here is not what the UI believed?
 *
 * `assertWorkspaceRole` puts a FACT about the caller in `details.roleReason`:
 * `no_workspace_membership` (no row here — they left or were removed) or
 * `insufficient_workspace_role` (their row no longer permits this — they were demoted).
 * Either MAY mean `userMembership` is stale, so a surface that sees one re-reads it (and only
 * re-reads it — see below) so it stops offering an act it no longer holds. `role_check_failed`
 * is deliberately excluded: the server could not find out, which says nothing about membership.
 */
const STALE_ROLE_REASONS: ReadonlySet<string> = new Set([
  ROLE_REASONS.NO_WORKSPACE_MEMBERSHIP,
  ROLE_REASONS.INSUFFICIENT_WORKSPACE_ROLE,
])

/**
 * ⚠️ A yes/no, deliberately — not WHICH reason (PR #237 round 7). The surface only TRIGGERS a
 * membership re-read with it (`workspaceService.refreshMembership`) and deletes nothing: the
 * re-read decides. `no_workspace_membership` is also what `assertWorkspaceRole`'s eventually
 * consistent `WorkspaceIndex` answers for a row written moments ago, and
 * `insufficient_workspace_role` is also what a correctly cached admin gets for an owner-only act,
 * so neither is on its own evidence the cache is wrong.
 */
export function isStaleRoleRefusal(res: { status?: number; details?: unknown } | null | undefined): boolean {
  if (!res || res.status !== 403) return false
  const details = res.details
  if (!details || typeof details !== 'object' || Array.isArray(details)) return false
  const roleReason = (details as { roleReason?: unknown }).roleReason
  return typeof roleReason === 'string' && STALE_ROLE_REASONS.has(roleReason)
}
